import * as React from 'react';
import range from 'lodash/range';
import { generateStyles, IParticle } from './styles';

const SIZE = 12; // max height for particle rectangles, diameter for particle circles
const PARTICLE_COUNT = 100;
const DURATION = 2200;
const COLORS = ['#FFC700', '#FF0000', '#2E3191', '#41BBC7'];

export interface ConfettiProps
	extends Omit<React.HTMLAttributes<HTMLDivElement>, 'ref'> {
	particleCount?: number;
	duration?: number;
	colors?: string[];
	particleSize?: number;
	force?: number;
	width?: number;
	zIndex?: number;
	onComplete?: () => void;
}

const createParticles = (count: number, colors: string[]): IParticle[] => {
	const increment = 360 / count;
	return range(count).map((index) => ({
		color: colors[index % colors.length],
		degree: increment * index,
	}));
};

function ConfettiExplosion({
	particleCount = PARTICLE_COUNT,
	duration = DURATION,
	colors = COLORS,
	particleSize = SIZE,
	force = 0.5,
	width = 1000,
	zIndex,
	onComplete,
	...props
}: ConfettiProps) {
	const [origin, setOrigin] = React.useState<{ top: number; left: number }>();
	const particles = React.useMemo(
		() => createParticles(particleCount, colors),
		[particleCount, colors]
	);
	const styles = React.useMemo(
		() => generateStyles({ particles, duration, particleSize, force, width }),
		[particles, duration, particleSize, force, width]
	);

	React.useEffect(() => {
		if (onComplete) {
			const timeout = setTimeout(onComplete, duration);
			return () => clearTimeout(timeout);
		}
		return undefined;
	}, [duration, onComplete]);

	const originRef = React.useCallback((node: HTMLDivElement) => {
		if (node) {
			const { top, left } = node.getBoundingClientRect();
			setOrigin({ top, left });
		}
	}, []);

	return (
		<div ref={originRef} className='w-0 h-0 relative' {...props}>
			{origin && (
				<>
					<style>{styles}</style>
					<div
						className='fixed inset-0 overflow-hidden pointer-events-none'
						style={{ zIndex }}>
						<div
							className='absolute'
							style={{
								top: origin.top,
								left: origin.left,
							}}>
							{particles.map((_, i) => (
								<div key={i} className={`particle-${i}`}>
									<div />
								</div>
							))}
						</div>
					</div>
				</>
			)}
		</div>
	);
}

export default ConfettiExplosion;
